import React from "react";
import classes from "./HeroSection.module.scss";
import { Breadcrumb } from "react-bootstrap";
import CustomImage from "../../CustomImage";
import { getOnboardingOrFormCtaProps } from "../../../utils/applyCtaButtonProps";
import { useRouter } from "next/router";
import { persistSourceContext } from "../../../utils/b2c/sourceTracking";
import { PRIMARY_SOURCE_B2C_WIDGET } from "../../../utils/b2c/constants";
import useIsMobile from "../../../hooks/useIsMobile";

const getCountryFromSlug = (slug: string) => {
  const parts = slug?.split("-") ?? [];
  const inIndex = parts.lastIndexOf("in");
  if (inIndex === -1) return "";
  const country = parts.slice(inIndex + 1).join("");
  return country.charAt(0).toUpperCase() + country.slice(1);
};

const HeroSection = ({ partnerCountryData }: any) => {
  const router = useRouter();
  const isMobile = useIsMobile(1024);
  const heroSectionData = partnerCountryData?.heroSection ?? {};
  const country = getCountryFromSlug(partnerCountryData?.slug);

  const applyCtaButtonProps = getOnboardingOrFormCtaProps(router, {
    btnLink: heroSectionData?.button?.link,
    formId: heroSectionData?.button?.formId,
    toOnboardingEnabled: heroSectionData?.button?.toOnboardingEnabled,
    sourceUrl: typeof window !== "undefined" ? window.location.pathname : "",
  });

  const ctaBtnClickHandler = (applyCtaButtonProps: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
    if (applyCtaButtonProps.mode === "onboarding") {
      persistSourceContext({
        sourceUrl: window.location.href,
        primarySource: PRIMARY_SOURCE_B2C_WIDGET,
        fifthStepVisible: heroSectionData?.button?.nearestOffice ?? true,
        coachingVisible: heroSectionData?.button?.lookingForCoaching ?? true,
      });
    }
    applyCtaButtonProps.onClick?.(e);
  };

  return (
    <>
      <section id="hero-section" className={classes["hero-section-container"]}>
        <picture className={classes["hero-picture"]}>
          <source media="(max-width: 767px)" srcSet={(isMobile ? heroSectionData?.mobileBgImg?.url : heroSectionData?.desktopBgImg?.url) ?? ""} />
          <img
            src={(isMobile ? heroSectionData?.mobileBgImg?.url : heroSectionData?.desktopBgImg?.url) ?? ""}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={"100%"}
            height={"100%"}
            alt={"hero.webp"}
          />
        </picture>

        <div className={classes["content-container"]}>
          <div className={classes["breadcrum-container"]}>
            <Breadcrumb>
              <Breadcrumb.Item style={{ opacity: 0.5 }} href="/">
                Home
              </Breadcrumb.Item>
              <Breadcrumb.Item href={"/partners/study-abroad-association"} className={classes["breadcrumb-item-2"]}>
                For Partners
              </Breadcrumb.Item>
              <Breadcrumb.Item href="#" className={classes["breadcrumb-item-2"]}>
                {heroSectionData?.breadcrumbCountryLabel ?? country}
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>

          {heroSectionData?.title && (
            <div className={classes.heading} dangerouslySetInnerHTML={{ __html: isMobile ? heroSectionData?.mobileTitle : heroSectionData?.title }} />
          )}
          {heroSectionData?.description && <div className={classes.description} dangerouslySetInnerHTML={{ __html: heroSectionData?.description }}></div>}

          {!isMobile && (
            <div className={classes["feature-container"]}>
              {heroSectionData?.feature?.map((feature: any, index: number) => (
                <div key={index} className={classes["feature"]}>
                  <div className={classes["icon-container"]}>
                    <CustomImage src={feature?.icon?.url ?? ""} height={20} width={20} alt={feature?.icon?.name ?? "icon.webp"} />
                  </div>
                  {feature?.title && <div className={classes["feature-title"]} dangerouslySetInnerHTML={{ __html: feature.title }}></div>}
                </div>
              ))}
            </div>
          )}
          <div className={classes["button-container"]}>
            <button onClick={ctaBtnClickHandler(applyCtaButtonProps)} className={applyCtaButtonProps.formClassName}>
              {heroSectionData?.button?.title || "Partner with us"}
            </button>

            {isMobile && (
              <div className={classes["feature-container-mobile-wrapper"]}>
                <div className={classes["feature-container-mobile"]}>
                  {heroSectionData?.feature?.map((feature: any) => (
                    <div className={classes["feature"]}>
                      <div className={classes["icon-container"]}>
                        <CustomImage src={feature?.icon?.url ?? ""} height={20} width={20} alt={feature?.icon?.name ?? "icon.webp"} />
                      </div>
                      {feature?.title && <div className={classes["feature-title"]} dangerouslySetInnerHTML={{ __html: feature.title }}></div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
